/**
 * Font Types & Local Font Access API
 *
 * The Local Font Access API allows web apps to enumerate system fonts.
 * Supported in Chrome 103+, Edge 103+, Opera 89+
 * Not supported in Firefox or Safari.
 */

// Font Access API types (not in standard TypeScript lib)
export interface FontData {
  family: string;
  fullName: string;
  postscriptName: string;
  style: string;
}

export interface QueryLocalFontsOptions {
  postscriptNames?: string[];
}

// Extend Window interface for Font Access API
declare global {
  interface Window {
    queryLocalFonts?: (options?: QueryLocalFontsOptions) => Promise<FontData[]>;
  }
}

// Parsed font family with fallbacks
export interface FontStack {
  primary: string;
  fallbacks: string[];
}

// Font metadata for the picker
export interface FontOption {
  family: string;
  displayName: string;
  source: 'system' | 'google' | 'embedded' | 'default';
  styles?: string[]; // Available styles: "Regular", "Bold", "Italic", etc.
}

// Generic font categories for fallbacks
export type GenericFontFamily =
  | 'serif'
  | 'sans-serif'
  | 'monospace'
  | 'cursive'
  | 'fantasy'
  | 'system-ui'
  | 'ui-serif'
  | 'ui-sans-serif'
  | 'ui-monospace'
  | 'ui-rounded';

/**
 * Default/fallback font list for browsers without Local Font Access API
 */
export const DEFAULT_FONTS: FontOption[] = [
  // Sans-serif
  { family: 'Inter', displayName: 'Inter', source: 'default' },
  { family: 'Manrope', displayName: 'Manrope', source: 'default' },
  { family: 'Space Grotesk', displayName: 'Space Grotesk', source: 'default' },
  { family: 'DM Sans', displayName: 'DM Sans', source: 'default' },
  { family: 'Poppins', displayName: 'Poppins', source: 'default' },
  { family: 'Roboto', displayName: 'Roboto', source: 'default' },
  { family: 'Open Sans', displayName: 'Open Sans', source: 'default' },
  { family: 'Lato', displayName: 'Lato', source: 'default' },
  { family: 'Montserrat', displayName: 'Montserrat', source: 'default' },
  { family: 'Source Sans Pro', displayName: 'Source Sans Pro', source: 'default' },
  { family: 'Nunito', displayName: 'Nunito', source: 'default' },
  { family: 'Raleway', displayName: 'Raleway', source: 'default' },
  // System fonts
  { family: 'system-ui', displayName: 'System UI', source: 'default' },
  { family: 'Arial', displayName: 'Arial', source: 'default' },
  { family: 'Helvetica Neue', displayName: 'Helvetica Neue', source: 'default' },
  { family: 'Segoe UI', displayName: 'Segoe UI', source: 'default' },
  // Serif
  { family: 'Georgia', displayName: 'Georgia', source: 'default' },
  { family: 'Times New Roman', displayName: 'Times New Roman', source: 'default' },
  { family: 'Playfair Display', displayName: 'Playfair Display', source: 'default' },
  { family: 'Merriweather', displayName: 'Merriweather', source: 'default' },
  { family: 'Lora', displayName: 'Lora', source: 'default' },
  // Monospace
  { family: 'JetBrains Mono', displayName: 'JetBrains Mono', source: 'default' },
  { family: 'Fira Code', displayName: 'Fira Code', source: 'default' },
  { family: 'Source Code Pro', displayName: 'Source Code Pro', source: 'default' },
  { family: 'Monaco', displayName: 'Monaco', source: 'default' },
  { family: 'Consolas', displayName: 'Consolas', source: 'default' },
  // Display
  { family: 'Bebas Neue', displayName: 'Bebas Neue', source: 'default' },
  { family: 'Anton', displayName: 'Anton', source: 'default' },
  { family: 'Oswald', displayName: 'Oswald', source: 'default' },
];

/**
 * Parse a CSS font-family string into primary + fallbacks
 */
export function parseFontStack(fontFamily: string): FontStack {
  const fonts = fontFamily
    .split(',')
    .map(f => f.trim().replace(/^["']|["']$/g, ''));

  return {
    primary: fonts[0] || 'sans-serif',
    fallbacks: fonts.slice(1),
  };
}

/**
 * Build a CSS font-family string from primary + fallbacks
 */
export function buildFontStack(primary: string, fallbacks: string[] = ['sans-serif']): string {
  const allFonts = [primary, ...fallbacks];
  return allFonts
    .map(f => f.includes(' ') && !isGenericFamily(f) ? `"${f}"` : f)
    .join(', ');
}

/**
 * Check if a font name is a generic CSS font family
 */
export function isGenericFamily(font: string): font is GenericFontFamily {
  return [
    'serif', 'sans-serif', 'monospace', 'cursive', 'fantasy',
    'system-ui', 'ui-serif', 'ui-sans-serif', 'ui-monospace', 'ui-rounded'
  ].includes(font);
}

/**
 * Get a sensible generic fallback based on font name heuristics
 */
export function getGenericFallback(fontFamily: string): GenericFontFamily {
  const lower = fontFamily.toLowerCase();

  if (lower.includes('mono') || lower.includes('code') || lower.includes('console')) {
    return 'monospace';
  }
  if (lower.includes('serif') && !lower.includes('sans')) {
    return 'serif';
  }
  if (lower.includes('cursive') || lower.includes('script') || lower.includes('hand')) {
    return 'cursive';
  }

  return 'sans-serif';
}

/**
 * Check if Local Font Access API is supported
 */
export function isLocalFontAccessSupported(): boolean {
  return typeof window !== 'undefined' && 'queryLocalFonts' in window;
}
